import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import type { ShouldRevalidateFunction } from 'react-router';
import { z } from 'zod';
import {
    getConversationByID,
    getConversationByParticipants,
} from '~/modules/chat/api/get-conversation.server';
import { UPDATE_CONVERSATION_API } from './remix-api.chat.update-conversation';
import { UPDATE_CONVERSATION_THUMBNAIL_API } from './remix-api.chat.update-thumbnail';
import { ADD_PARTICIPANTS_API } from './remix-api.chat.add-participants';
import { REMOVE_PARTICIPANT_API } from './remix-api.chat.remove-participant';
import { INVITE_TO_GROUP_CHAT_API } from './remix-api.chat.invite-to-group-chat';
import { UPDATE_CONVERSATION_STATUS_API } from './remix-api.chat.update-conversation-status';
import { MUTE_CONVERSATION_API } from './remix-api.chat.mute';
import { MARK_CONVERSATION_READ_API } from './remix-api.chat.mark-conversation-read';
import { MARK_CONVERSATION_UNREAD_API } from './remix-api.chat.mark-conversation-unread';
import { REACT_MESSAGE_API } from './remix-api.chat.react-message';
import { DELETE_MESSAGE_API } from './remix-api.chat.delete-message';
import { UPDATE_CONVERSATION_SETTINGS_API } from './remix-api.chat.update-settings';

export const CONVERSATION_API = '/remix-api/chat/conversation';

const ID_SCHEMA = z.coerce.number().int().positive();
const QUERY_SCHEMA = z
    .object({
        id: ID_SCHEMA.nullish(),
        professionalID: ID_SCHEMA.nullish(),
        participantMemberIDs: z.array(ID_SCHEMA).nullish(),
        participantProfessionalIDs: z.array(ID_SCHEMA).nullish(),
        beforeID: ID_SCHEMA.nullish(),
    })
    .refine(
        (data) => {
            return (
                !!data.id ||
                !!data.participantMemberIDs?.length ||
                !!data.participantProfessionalIDs?.length
            );
        },
        {
            message:
                'Either `id`, `participantMemberIDs`, or `participantProfessionalIDs` must be provided',
        },
    );

/**
 * API endpoint to get a conversation between the user and another participant(s).
 * If both `id` and `participantMemberIDs`/`participantProfessionalIDs` are provided,
 * the `id` will be used. Use with the `useConversation` hook.
 * @see {@link packages/web/app/modules/chat/context/ConversationContext.tsx}
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        id: url.searchParams.get('id'),
        professionalID: url.searchParams.get('professionalID'),
        participantMemberIDs: url.searchParams.getAll('participantMemberIDs'),
        participantProfessionalIDs: url.searchParams.getAll(
            'participantProfessionalIDs',
        ),
        beforeID: url.searchParams.get('beforeID'),
    });
    if (!result.success) {
        return routerData(null, { status: 400 });
    }

    try {
        const conversation = result.data.id
            ? await getConversationByID({
                  context,
                  id: result.data.id,
                  professionalID: result.data.professionalID,
                  beforeID: result.data.beforeID ?? undefined,
              })
            : await getConversationByParticipants({
                  context,
                  memberIDs: result.data.participantMemberIDs ?? [],
                  professionalIDs: result.data.participantProfessionalIDs ?? [],
              });

        return { conversation };
    } catch (error) {
        logger.error(`Error in ${CONVERSATION_API}`, { error });
        return routerData(null, { status: 500 });
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
    defaultShouldRevalidate,
    formAction,
}) => {
    if (
        formAction === UPDATE_CONVERSATION_API ||
        formAction === UPDATE_CONVERSATION_THUMBNAIL_API ||
        formAction === ADD_PARTICIPANTS_API ||
        formAction === REMOVE_PARTICIPANT_API ||
        formAction === INVITE_TO_GROUP_CHAT_API ||
        formAction === UPDATE_CONVERSATION_STATUS_API ||
        formAction === MUTE_CONVERSATION_API ||
        formAction === MARK_CONVERSATION_READ_API ||
        formAction === MARK_CONVERSATION_UNREAD_API ||
        formAction === REACT_MESSAGE_API ||
        formAction === DELETE_MESSAGE_API ||
        formAction === UPDATE_CONVERSATION_SETTINGS_API
    ) {
        // Let the conversation cache update the conversation instead.
        return false;
    }
    return defaultShouldRevalidate;
};
